import React, { useState, useEffect } from "react";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  AddCircleOutlined,
  Delete,
  Refresh,
  WarningRounded,
} from "@mui/icons-material";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  doc,
  addDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase-config";
import dayjs from "dayjs";

const grades = [
  "Prep",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
  "Pass Out",
];

const currentYear = dayjs().year();

const ClassAssignment = () => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [studentCountsByGrade, setStudentCountsByGrade] = useState({});
  const [studentCountsByClass, setStudentCountsByClass] = useState({});
  const [attendanceCounts, setAttendanceCounts] = useState({});
  const [isClassesEmpty, setIsClassesEmpty] = useState(false);
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [showSetupYearButton, setShowSetupYearButton] = useState(false);

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchStudentCounts();
    fetchAttendanceCounts();
  }, []);

  const fetchClasses = async () => {
    try {
      const classesCollection = collection(db, "classes");
      const classesSnapshot = await getDocs(classesCollection);

      if (classesSnapshot.empty) {
        setIsClassesEmpty(true);
      } else {
        setIsClassesEmpty(false);
      }

      const classData = classesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClasses(classData);
    } catch (error) {
      console.error("Error fetching classes: ", error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const q = query(
        collection(db, "users"),
        where("roles", "array-contains", "teacher"),
      );
      const snapshot = await getDocs(q);
      const teacherData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeachers(teacherData);
    } catch (error) {
      console.error("Error fetching teachers: ", error);
    }
  };

  const fetchStudentCounts = async () => {
    try {
      const countsByGrade = {};
      const countsByClass = {};
      var previousSchoolGradeYears = 0;
      const studentsCollection = collection(db, "students");
      const q = query(studentsCollection, where("status", "==", "Active"));
      const studentsSnapshot = await getDocs(q);
      studentsSnapshot.forEach((doc) => {
        const studentData = doc.data();
        const studentSchoolGrade = studentData.schoolGrade;
        const studentClass = studentData.class;

        if (studentData.schoolGradeYear < currentYear) {
          previousSchoolGradeYears++;
        }

        if (countsByGrade[studentSchoolGrade]) {
          countsByGrade[studentSchoolGrade]++;
        } else {
          countsByGrade[studentSchoolGrade] = 1;
        }

        if (countsByClass[studentClass]) {
          countsByClass[studentClass]++;
        } else {
          countsByClass[studentClass] = 1;
        }
      });
      setShowSetupYearButton(previousSchoolGradeYears > 0);
      setStudentCountsByGrade(countsByGrade);
      setStudentCountsByClass(countsByClass);
    } catch (error) {
      console.error("Error fetching student counts: ", error);
    }
  };

  const fetchAttendanceCounts = async () => {
    try {
      const currentDate = dayjs();
      const lastSunday =
        currentDate.day() === 0
          ? currentDate
          : currentDate.subtract(currentDate.day(), "day");
      const formattedDate = lastSunday.format("YYYY-MM-DD");

      const attendanceCounts = {};
      const classesSnapshot = await getDocs(collection(db, "classes"));
      const classData = classesSnapshot.docs.map((doc) => doc.data());

      for (const cls of classData) {
        const attendanceDocRef = doc(
          db,
          "attendance",
          `${formattedDate}-${cls.name}`,
        );
        const attendanceDoc = await getDoc(attendanceDocRef);

        if (attendanceDoc.exists()) {
          const records = attendanceDoc.data().records;
          const total = Object.keys(records).length;
          const present = Object.values(records).filter(
            (value) => value === true,
          ).length;
          attendanceCounts[cls.name] = `${present}/${total}`;
        } else {
          attendanceCounts[cls.name] = "0/0";
        }
      }

      setAttendanceCounts(attendanceCounts);
    } catch (error) {
      console.error("Error fetching attendance counts: ", error);
    }
  };

  const createDefaultClasses = async () => {
    try {
      const filteredGrades = grades.filter((grade) => grade !== "Pass Out");
      const defaultClasses = filteredGrades.map((grade) => {
        return { grade, name: grade, teachers: [] };
      });

      const batch = defaultClasses.map((cls) =>
        addDoc(collection(db, "classes"), cls),
      );
      await Promise.all(batch);
      setClasses(defaultClasses);
      setIsClassesEmpty(false);

      // Update students' class to match their schoolGrade
      const studentsCollection = collection(db, "students");
      const q = query(studentsCollection, where("status", "==", "Active"));
      const studentsSnapshot = await getDocs(q);
      const batchUpdate = writeBatch(db);
      const currentYear = new Date().getFullYear();

      studentsSnapshot.forEach((doc) => {
        const studentData = doc.data();
        const studentRef = doc.ref;
        batchUpdate.update(studentRef, {
          class: studentData.schoolGrade,
          classYear: currentYear,
        });
      });
      await batchUpdate.commit();
    } catch (error) {
      console.error("Error creating default classes: ", error);
    }
  };

  const addClass = async (grade) => {
    try {
      // If only one class is present in a grade, rename it to add 'A'
      if (classes.filter((cls) => cls.grade === grade).length === 1) {
        const existingClass = classes.find((cls) => cls.grade === grade);
        const existingClassName = `${grade}A`;
        const { id, ...existingClassData } = existingClass; // Exclude the id from the data
        await setDoc(doc(db, "classes", id), {
          ...existingClassData,
          name: existingClassName,
        });
        setClasses((prevClasses) =>
          prevClasses.map((cls) =>
            cls.grade === grade ? { ...cls, name: existingClassName } : cls,
          ),
        );
      }
      const newClassName = `${grade}${String.fromCharCode(
        65 + classes.filter((cls) => cls.grade === grade).length,
      )}`;
      const newClass = { grade, name: newClassName, teachers: [] };
      const newClassDocRef = await addDoc(collection(db, "classes"), newClass);
      setClasses((prevClasses) => [
        ...prevClasses,
        { id: newClassDocRef.id, ...newClass },
      ]);
    } catch (error) {
      console.error("Error adding class: ", error);
    }
  };

  const deleteClass = async (grade) => {
    try {
      // Find all classes for the given grade
      const classQuery = query(
        collection(db, "classes"),
        where("grade", "==", grade),
      );
      const classSnapshot = await getDocs(classQuery);
      const classDocs = classSnapshot.docs;

      if (classDocs.length > 0) {
        // Sort classes alphabetically by name and get the last class
        const sortedClasses = classDocs.sort((a, b) =>
          a.data().name.localeCompare(b.data().name),
        );
        const lastClassDoc = sortedClasses[sortedClasses.length - 1];
        const lastClassName = lastClassDoc.data().name;

        // Delete the last class
        await deleteDoc(lastClassDoc.ref);
        let updatedClasses = classes.filter(
          (cls) => cls.name !== lastClassName,
        );

        // Check if only one class is left for the grade and rename it back to the original grade name
        const remainingClasses = updatedClasses.filter(
          (cls) => cls.grade === grade,
        );
        if (remainingClasses.length === 1) {
          const remainingClass = remainingClasses[0];
          const originalClassName = grade;
          const { id, ...remainingClassData } = remainingClass; // Exclude the id from the data
          await setDoc(doc(db, "classes", id), {
            ...remainingClassData,
            name: originalClassName,
          });
          updatedClasses = updatedClasses.map((cls) =>
            cls.id === remainingClass.id
              ? { ...cls, name: originalClassName }
              : cls,
          );
        }

        // Update the state with the final classes
        setClasses(updatedClasses);
      }
    } catch (error) {
      console.error("Error deleting class: ", error);
    }
  };

  const reassignStudentsToClasses = async (grade) => {
    try {
      const studentsQuery = query(
        collection(db, "students"),
        where("schoolGrade", "==", grade),
      );
      const studentsSnapshot = await getDocs(studentsQuery);
      const students = studentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const gradeClasses = classes.filter((cls) => cls.grade === grade);
      const currentYear = new Date().getFullYear();
      const batchUpdate = writeBatch(db);

      const activeStudents = [];
      const inactiveStudents = [];

      // Step 1: Filter students into two groups
      students.forEach((student) => {
        if (grade === "Pass Out" || student.status !== "Active") {
          inactiveStudents.push(student);
        } else {
          activeStudents.push(student);
        }
      });

      // Step 2: Update inactive students
      inactiveStudents.forEach((student) => {
        const studentRef = doc(db, "students", student.id);
        batchUpdate.update(studentRef, {
          class: "",
          classYear: null,
        });
      });

      // Step 3: Update active students and assign them to classes
      activeStudents.forEach((student, index) => {
        const studentRef = doc(db, "students", student.id);
        const assignedClass = gradeClasses[index % gradeClasses.length].name;
        batchUpdate.update(studentRef, {
          class: assignedClass,
          classYear: currentYear,
        });
      });

      await batchUpdate.commit();
      // Refresh data after reassigning students
      fetchClasses();
      fetchStudentCounts();
    } catch (error) {
      console.error("Error reassigning students to classes: ", error);
    }
  };

  const assignTeacher = async (classId, teacherId) => {
    try {
      const classDocRef = doc(db, "classes", classId);
      const classDoc = await getDoc(classDocRef);
      if (classDoc.exists()) {
        const classData = classDoc.data();
        const updatedTeachers = classData.teachers
          ? [...classData.teachers, teacherId]
          : [teacherId];
        await setDoc(
          classDocRef,
          { teachers: updatedTeachers },
          { merge: true },
        );
        setClasses((prevClasses) =>
          prevClasses.map((cls) =>
            cls.id === classId ? { ...cls, teachers: updatedTeachers } : cls,
          ),
        );
        setSelectedClass((prevSelectedClass) => ({
          ...prevSelectedClass,
          teachers: updatedTeachers,
        }));
      }
    } catch (error) {
      console.error("Error assigning teacher: ", error);
    }
  };

  const removeTeacher = async (classId, teacherId) => {
    try {
      const classDocRef = doc(db, "classes", classId);
      const classDoc = await getDoc(classDocRef);
      if (classDoc.exists()) {
        const classData = classDoc.data();
        const updatedTeachers = classData.teachers.filter(
          (id) => id !== teacherId,
        );
        await setDoc(
          classDocRef,
          { teachers: updatedTeachers },
          { merge: true },
        );
        setClasses((prevClasses) =>
          prevClasses.map((cls) =>
            cls.id === classId ? { ...cls, teachers: updatedTeachers } : cls,
          ),
        );
        setSelectedClass((prevSelectedClass) => ({
          ...prevSelectedClass,
          teachers: updatedTeachers,
        }));
      }
    } catch (error) {
      console.error("Error removing teacher: ", error);
    }
  };

  const setupYear = async () => {
    try {
      const batchUpdate = writeBatch(db);

      // Update student grades
      const studentsCollection = collection(db, "students");
      const studentsSnapshot = await getDocs(studentsCollection);

      studentsSnapshot.forEach((doc) => {
        const studentData = doc.data();
        const studentRef = doc.ref;

        if (
          studentData.status === "Active" &&
          studentData.schoolGrade !== "Pass Out" &&
          studentData.schoolGradeYear < currentYear
        ) {
          const currentGradeIndex = grades.indexOf(studentData.schoolGrade);
          const nextGrade = grades[currentGradeIndex + 1] || "Pass Out";

          batchUpdate.update(studentRef, {
            schoolGrade: nextGrade,
            status: nextGrade === "Pass Out" ? "Pass Out" : "Active",
            schoolGradeYear: currentYear,
            feesPaid: false,
          });
        }
      });

      // Increment experience by 1 for all teachers and principals
      const usersCollection = collection(db, "users");
      const teacherQuery = query(
        usersCollection,
        where("roles", "array-contains", "teacher"),
      );
      const principalQuery = query(
        usersCollection,
        where("roles", "array-contains", "principal"),
      );

      const [teacherSnapshot, principalSnapshot] = await Promise.all([
        getDocs(teacherQuery),
        getDocs(principalQuery),
      ]);

      const userSet = new Set();
      const userDataMap = new Map();

      teacherSnapshot.forEach((doc) => {
        userSet.add(doc.id);
        userDataMap.set(doc.id, doc.data());
      });
      principalSnapshot.forEach((doc) => {
        userSet.add(doc.id);
        userDataMap.set(doc.id, doc.data());
      });

      userSet.forEach((userId) => {
        const userRef = doc(db, "users", userId);
        const userData = userDataMap.get(userId);
        // ensure numeric addition even if stored as string
        const currentExperience = parseInt(
          userData.catechismExperienceInAustralia || 0,
          10,
        );
        batchUpdate.update(userRef, {
          catechismExperienceInAustralia: currentExperience + 1,
        });
      });

      await batchUpdate.commit();
      alert("Year setup completed successfully!");
      fetchClasses();
      fetchStudentCounts();
    } catch (error) {
      console.error("Error setting up year: ", error);
      alert("Failed to setup year.");
    }
  };

  const handleOpenTeacherDialog = (classItem) => {
    setSelectedClass(classItem);
    setTeacherDialogOpen(true);
  };

  const handleCloseTeacherDialog = () => {
    setTeacherDialogOpen(false);
    setSelectedClass(null);
  };

  const handleOpenDeleteDialog = (classItem) => {
    setClassToDelete(classItem);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setClassToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (classToDelete) {
      deleteClass(classToDelete.grade);
    }
    handleCloseDeleteDialog();
  };

  return (
    <Container maxWidth="lg" className="app-wrapper">
      {isClassesEmpty && (
        <Button
          variant="outlined"
          color="primary"
          onClick={createDefaultClasses}
        >
          Create default classes
        </Button>
      )}
      {showSetupYearButton && (
        <Button variant="outlined" color="secondary" onClick={setupYear}>
          Setup Year
        </Button>
      )}
      {/* <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          grades.forEach((grade) => {
            reassignStudentsToClasses(grade);
          });
        }}
      >
        Reassign Students to Classes
      </Button> */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Grade</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Students (Grade)</TableCell>
              <TableCell>Students (Class)</TableCell>
              <TableCell>Teachers</TableCell>
              <TableCell>Attendance</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grades
              .filter((grade) => grade !== "Pass Out")
              .map((grade) =>
                classes
                  .filter((cls) => cls.grade === grade)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((classItem) => (
                    <TableRow key={classItem.id}>
                      <TableCell>{classItem.grade}</TableCell>
                      <TableCell>{classItem.name}</TableCell>
                      <TableCell>
                        {studentCountsByGrade[classItem.grade] || 0}
                      </TableCell>
                      <TableCell>
                        {studentCountsByClass[classItem.name] || 0}
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleOpenTeacherDialog(classItem)}
                        >
                          {classItem.teachers.length}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {attendanceCounts[classItem.name] === "0/0" ? (
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {attendanceCounts[classItem.name]}
                            <WarningRounded
                              style={{ color: "red", marginLeft: 4 }}
                            />
                          </div>
                        ) : (
                          attendanceCounts[classItem.name] || "0/0"
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Add a Class to this Grade">
                          <IconButton onClick={() => addClass(classItem.grade)}>
                            <AddCircleOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reassign Students in Grade">
                          <IconButton
                            onClick={() =>
                              reassignStudentsToClasses(classItem.grade)
                            }
                          >
                            <Refresh />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove Class">
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(classItem)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )),
              )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Teacher Dialog */}
      <Dialog open={teacherDialogOpen} onClose={handleCloseTeacherDialog}>
        <DialogTitle>Manage Teachers</DialogTitle>
        <DialogContent>
          {selectedClass && (
            <div>
              <Typography variant="h6">{selectedClass.name}</Typography>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                {selectedClass.teachers.map((teacherId) => {
                  const teacher = teachers.find((t) => t.id === teacherId);
                  return (
                    <Card
                      key={teacherId}
                      variant="outlined"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 8px",
                      }}
                    >
                      <CardContent style={{ padding: "0 8px" }}>
                        <Typography variant="body2">
                          {teacher?.displayName || teacher?.email}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <IconButton
                          onClick={() =>
                            removeTeacher(selectedClass.id, teacherId)
                          }
                        >
                          <Delete />
                        </IconButton>
                      </CardActions>
                    </Card>
                  );
                })}
              </div>
              <FormControl fullWidth id="add-teacher-form-control">
                <InputLabel id="add-teacher-label">Add Teacher</InputLabel>
                <Select
                  labelId="add-teacher-label"
                  id="add-teacher-select"
                  value=""
                  onChange={(e) =>
                    assignTeacher(selectedClass.id, e.target.value)
                  }
                >
                  <MenuItem value="" id="add-teacher-none">
                    <em>None</em>
                  </MenuItem>
                  {teachers
                    .filter(
                      (teacher) => !selectedClass.teachers.includes(teacher.id),
                    )
                    .map((teacher) => (
                      <MenuItem
                        key={teacher.id}
                        value={teacher.id}
                        id={`add-teacher-${teacher.id}`}
                      >
                        {teacher.displayName || teacher.email}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTeacherDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the class {classToDelete?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClassAssignment;
